import {
    BLOCK_CODE_LINE,
    BLOCK_EXPANDABLE,
    BLOCK_LIST_ITEM,
    BLOCK_PARAGRAPH,
    BLOCK_TABS,
    FRAGMENT_CAPTION,
    LIST_BLOCKS,
} from '@gitbook/doc-core';
import { isBlockRangeSelection } from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';

import Hotkeys from '../../../../../slate-hotkeys/src';

// These are blocks where a block range selection should be
// allowed to traverse to descendant blocks, e.g. to select
// a nested list item in a list or the content of an expandable
// block.
//
// Keeping to a restricted allowed list to avoid
// slightly strange user experience, e.g.
// selecting the caption of an images or file upload block
//
// Also restricting access to table/card
// as that is handled by tableSelection.
const BLOCK_RANGE_ACCESSIBLE_BLOCKS_LIST = [
    BLOCK_EXPANDABLE,
    BLOCK_TABS,
    ...LIST_BLOCKS,
    BLOCK_LIST_ITEM,
];

/*
 * Plugin to handle some exceptional cases in block range selection.
 *
 * For example, restricting the traversal to descendants for certain blocks.
 */
export const blockSelectionPlugin: Plugin = {
    onSelect: (_, change) => {
        const { value } = change;
        const { document, selection } = value;

        if (!isBlockRangeSelection(selection)) {
            return;
        }

        if (selection.isCollapsed) {
            const key = selection.anchorBlock || selection.focusBlock;
            const block = key ? document.getNode(key) : null;
            if (!block || !block.isBlock()) {
                return;
            }

            switch (block.type) {
                case BLOCK_PARAGRAPH:
                    // where a block selection is created on a caption fragment, move the selection to the parent (image or embed)
                    if (document.getFragmentParent(block.key)?.fragment === FRAGMENT_CAPTION) {
                        return change.select(
                            selection.moveSelectionToAncestor(change.value.document)
                        );
                    }

                    // where a paragraph is a child of a list item, move the block selection on to the list item
                    if (document.getParent(block.key)?.type === BLOCK_LIST_ITEM) {
                        return change.select(
                            selection.moveSelectionToAncestor(change.value.document)
                        );
                    }
                    break;
            }
        }

        // where a block selection is created on one or more lines of code, move the selection to the parent (code block)
        if (value.blocks.every((block) => block.type === BLOCK_CODE_LINE)) {
            return change.select(selection.moveSelectionToAncestor(change.value.document));
        }
    },
    onKeyDown: (event, change) => {
        const { value } = change;
        const { document, selection } = value;

        if (!isBlockRangeSelection(selection)) {
            return;
        }

        if (selection.isCollapsed) {
            const key = selection.anchorBlock || selection.focusBlock;
            const block = key ? document.getNode(key) : null;
            if (!block || !block.isBlock()) {
                return;
            }

            // if the user is trying to traverse to a descendant block
            // in a block range selection and block is not in the allowed list
            // then restrict the default behaviour.
            if (Hotkeys.isCollapseCharForward(event)) {
                if (BLOCK_RANGE_ACCESSIBLE_BLOCKS_LIST.includes(block.type)) {
                    return;
                }
                event.preventDefault();
                return change;
            }
        }
    },
};
